import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchUser, logout } from "../redux/authSlice";
import { fetchEvents, selectEvents } from "../redux/eventsSlice";
import MeetingsSection from "./MeetingsSection";
import AlertConfiguration from "./AlertConfiguration";
import ConnectCalendarButton from "./ConnectCalendarButton";
import WorkingHoursPopup from "./WorkingHoursPopup";
import { getLoginUrl } from "../api/user";

const Dashboard = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { user, token } = useSelector(state => state.auth);
	const { events, loading, error } = useSelector(selectEvents);
	const [menuOpen, setMenuOpen] = useState(false);
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		if (!token) {
			navigate("/login");
			return;
		}
		dispatch(fetchUser(token));
		dispatch(fetchEvents(token));
	}, [token, dispatch, navigate]);

	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth <= 768);
		};

		checkMobile();
		window.addEventListener("resize", checkMobile);

		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	// Close menu when clicking outside
	useEffect(() => {
		const handleClickOutside = event => {
			if (menuOpen && !event.target.closest(".mobile-menu-container")) {
				setMenuOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [menuOpen]);

	const handleLogout = () => {
		dispatch(logout());
		navigate("/login");
	};

	// Redirect to Microsoft OAuth
	const connectCalendar = async provider => {
		const { url } = await getLoginUrl(token, provider);
		window.location.href = url;
	};

	// Calendar connection handlers
	const handleGoogleCalendarAction = () => {
		const isConnected = user?.googleId || false;
		const calendarAccessGranted = user?.googleCalendarAccessGranted || false;

		if (isConnected && calendarAccessGranted) {
			// Handle sync toggle
			console.log("Toggle Google Calendar sync on/off");
			// TODO: Implement sync toggle API call
			// toggleCalendarSync('google', !user.googleCalendarSyncEnabled);
		} else if (isConnected && !calendarAccessGranted) {
			// Handle grant access
			console.log("Grant Google Calendar access");
			// TODO: Implement calendar access grant
			connectCalendar("google");
		} else {
			// Handle initial connection
			console.log("Connect Google Calendar");
			// TODO: Implement Google Calendar connection
			connectCalendar("google");
		}
	};

	const handleMicrosoftCalendarAction = () => {
		const isConnected = user?.microsoftId || false;
		const calendarAccessGranted = user?.microsoftCalendarAccessGranted || false;

		if (isConnected && calendarAccessGranted) {
			// Handle sync toggle
			console.log("Toggle Microsoft Calendar sync on/off");
			// TODO: Implement sync toggle API call
			// toggleCalendarSync('microsoft', !user.microsoftCalendarSyncEnabled);
		} else if (isConnected && !calendarAccessGranted) {
			// Handle grant access
			console.log("Grant Microsoft Calendar access");
			// TODO: Implement calendar access grant
			connectCalendar("microsoft");
		} else {
			// Handle initial connection
			console.log("Connect Microsoft Calendar");
			// TODO: Implement Microsoft Calendar connection
			connectCalendar("microsoft");
		}
	};

	const getResponsiveStyles = () => {
		const baseStyles = styles;

		if (window.innerWidth <= 480) {
			return {
				...baseStyles,
				topNav: {
					...baseStyles.topNav,
					padding: "0.75rem 1rem",
				},
				brandText: {
					...baseStyles.brandText,
					fontSize: "18px",
					lineHeight: "36px",
				},
				mainContent: {
					...baseStyles.mainContent,
					padding: "0 1rem 4rem 1rem",
					paddingLeft: "1rem",
				},
				welcomeAndMeetingsContainer: {
					...baseStyles.welcomeAndMeetingsContainer,
					flexDirection: "column",
					gap: "1.5rem",
					marginRight: "0",
					paddingRight: "0",
					minHeight: "auto",
					alignItems: "flex-start",
				},
				welcomeSection: {
					...baseStyles.welcomeSection,
					minWidth: "auto",
					width: "100%",
					paddingTop: "1rem",
				},
				welcomeTitle: {
					...baseStyles.welcomeTitle,
					fontSize: "2rem", // 32px
					lineHeight: "1.2",
					marginBottom: "0.5rem",
				},
				welcomeSubtitle: {
					...baseStyles.welcomeSubtitle,
					fontSize: "1rem", // 16px
					maxWidth: "100%",
					lineHeight: "1.4",
				},
				meetingsWrapper: {
					...baseStyles.meetingsWrapper,
					marginRight: "0",
					paddingRight: "0",
					width: "100%",
				},
				bottomSection: {
					...baseStyles.bottomSection,
					gridTemplateColumns: "1fr",
					gap: "1rem",
					marginTop: "1rem",
					paddingTop: "1rem",
				},
				sectionCard: {
					...baseStyles.sectionCard,
					padding: "1.5rem",
					minHeight: "auto",
				},
				horizontalDivider: {
					...baseStyles.horizontalDivider,
					marginTop: "50px", // Instead of the default "2rem"
				},
			};
		} else if (window.innerWidth <= 768) {
			return {
				...baseStyles,
				topNav: {
					...baseStyles.topNav,
					padding: "1rem 1.5rem",
				},
				brandText: {
					...baseStyles.brandText,
					fontSize: "20px",
					lineHeight: "40px",
				},
				mainContent: {
					...baseStyles.mainContent,
					padding: "0 2rem 4rem 2rem",
					paddingLeft: "2rem",
				},
				welcomeAndMeetingsContainer: {
					...baseStyles.welcomeAndMeetingsContainer,
					flexDirection: "column",
					gap: "2rem",
					marginRight: "0",
					paddingRight: "0",
					minHeight: "auto",
					alignItems: "flex-start",
				},
				welcomeSection: {
					...baseStyles.welcomeSection,
					minWidth: "auto",
					width: "100%",
					paddingTop: "2rem",
				},
				welcomeTitle: {
					...baseStyles.welcomeTitle,
					fontSize: "2.5rem", // 40px
					lineHeight: "1.1",
				},
				welcomeSubtitle: {
					...baseStyles.welcomeSubtitle,
					fontSize: "1.2rem", // 19px
					maxWidth: "100%",
				},
				meetingsWrapper: {
					...baseStyles.meetingsWrapper,
					marginRight: "0",
					paddingRight: "0",
					width: "100%",
				},
				bottomSection: {
					...baseStyles.bottomSection,
					gridTemplateColumns: "1fr",
					gap: "1.5rem",
				},
				sectionCard: {
					...baseStyles.sectionCard,
					padding: "1.5rem",
				},
				horizontalDivider: {
					...baseStyles.horizontalDivider,
					marginTop: "50px", // Instead of the default "2rem"
				},
			};
		} else if (window.innerWidth <= 900) {
			return {
				...baseStyles,
				welcomeAndMeetingsContainer: {
					...baseStyles.welcomeAndMeetingsContainer,
					flexDirection: "column",
					gap: "2rem",
					marginRight: "0",
					paddingRight: "0",
					minHeight: "auto",
					alignItems: "flex-start",
				},
				bottomSection: {
					...baseStyles.bottomSection,
					gridTemplateColumns: "1fr",
					gap: "1.5rem",
				},
				horizontalDivider: {
					...baseStyles.horizontalDivider,
					marginTop: "50px", // Instead of the default "2rem"
				},
				// ... other 900px styles
			};
		} else if (window.innerWidth <= 1077) {
			return {
				...baseStyles,
				mainContent: {
					...baseStyles.mainContent,
					padding: "0 3rem 6rem 3rem",
					paddingLeft: "60px",
				},
				welcomeAndMeetingsContainer: {
					...baseStyles.welcomeAndMeetingsContainer,
					gap: "2rem",
					marginRight: "-3rem",
				},
				welcomeSection: {
					...baseStyles.welcomeSection,
					minWidth: "350px",
				},
				welcomeTitle: {
					...baseStyles.welcomeTitle,
					fontSize: "3rem", // 48px
				},
				welcomeSubtitle: {
					...baseStyles.welcomeSubtitle,
					fontSize: "1.5rem", // 24px
				},
				bottomSection: {
					...baseStyles.bottomSection,
					gridTemplateColumns: "1fr 1fr",
					gap: "1.5rem",
				},
			};
		}

		return baseStyles;
	};

	const responsiveStyles = getResponsiveStyles();

	return (
		<div style={responsiveStyles.pageContainer}>
			{/* Header */}
			<div style={responsiveStyles.topNav}>
				<h1 style={responsiveStyles.brandText}>Hyacinthe.io</h1>

				{isMobile ? (
					<div className="mobile-menu-container" style={responsiveStyles.mobileMenuContainer}>
						<button style={responsiveStyles.hamburgerButton} onClick={() => setMenuOpen(!menuOpen)}>
							<div style={responsiveStyles.hamburgerLine}></div>
							<div style={responsiveStyles.hamburgerLine}></div>
							<div style={responsiveStyles.hamburgerLine}></div>
						</button>

						{menuOpen && (
							<div style={responsiveStyles.mobileMenu}>
								<div style={responsiveStyles.mobileUserProfile}>
									{user?.picture ? (
										<img src={user.picture} alt="Profile" style={responsiveStyles.mobileProfileImage} />
									) : (
										<div style={responsiveStyles.mobileAvatarFallback}>
											{user?.name
												?.split(" ")
												.map(n => n[0])
												.join("") || "U"}
										</div>
									)}
									<div style={responsiveStyles.mobileUserInfo}>
										<div style={responsiveStyles.mobileUserName}>{user?.name}</div>
										<div style={responsiveStyles.mobileUserEmail}>{user?.email}</div>
									</div>
								</div>
								<button onClick={handleLogout} style={responsiveStyles.mobileLogoutButton}>
									<img src="./logout.svg" alt="Logout" style={responsiveStyles.mobileLogoutIcon} />
									Logout
								</button>
							</div>
						)}
					</div>
				) : (
					<div style={responsiveStyles.userNav}>
						<div style={responsiveStyles.profileInfo}>
							<div style={responsiveStyles.userName}>{user?.name}</div>
							<div style={responsiveStyles.userEmail}>{user?.email}</div>
						</div>
						<div style={responsiveStyles.profileAvatar}>
							{user?.picture ? (
								<img src={user.picture} alt="Profile" style={responsiveStyles.profileImage} />
							) : (
								<div style={responsiveStyles.avatarFallback}>
									{user?.name
										?.split(" ")
										.map(n => n[0])
										.join("") || "U"}
								</div>
							)}
						</div>
						<button onClick={handleLogout} style={responsiveStyles.logoutButton}>
							<img src="./logout.svg" alt="Logout" style={responsiveStyles.logoutIcon} />
						</button>
					</div>
				)}
			</div>

			{/* Main Content */}
			<div style={responsiveStyles.mainContent}>
				{/* Welcome and Meetings Section - Side by Side */}
				<div style={responsiveStyles.welcomeAndMeetingsContainer}>
					{/* Welcome Section */}
					<div style={responsiveStyles.welcomeSection}>
						<h1 style={responsiveStyles.welcomeTitle}>Hey {user?.name?.split(" ")[0] || "there"}!</h1>
						<p style={responsiveStyles.welcomeSubtitle}>Here are your upcoming meetings.</p>
					</div>

					{/* Meetings Section */}
					<div style={responsiveStyles.meetingsWrapper}>
						<MeetingsSection events={events} loading={loading} error={error} token={token} />
					</div>
				</div>

				{/* Horizontal Divider - Separate from meetings container */}
				<div style={responsiveStyles.horizontalDivider}></div>

				{/* Bottom Section */}
				<div style={responsiveStyles.bottomSection}>
					<div style={responsiveStyles.sectionCard}>
						<AlertConfiguration user={user || {}} />
					</div>

					<div style={responsiveStyles.sectionCard}>
						<div style={responsiveStyles.sectionTitle}>Calendar Connections</div>
						<div style={responsiveStyles.calendarConnections}>
							<ConnectCalendarButton
								type="google"
								isConnected={user?.googleId || false}
								calendarAccessGranted={user?.googleCalendarAccessGranted || false}
								onClick={handleGoogleCalendarAction}
							/>
							<ConnectCalendarButton
								type="microsoft"
								isConnected={user?.microsoftId || false}
								calendarAccessGranted={user?.microsoftCalendarAccessGranted || false}
								onClick={handleMicrosoftCalendarAction}
							/>
						</div>
					</div>

					<div style={responsiveStyles.sectionCard}>
						<div style={responsiveStyles.sectionTitle}>Working hours</div>
						<WorkingHoursPopup />
					</div>
				</div>
			</div>
		</div>
	);
};

const styles = {
	pageContainer: {
		minHeight: "100vh",
		// background: "linear-gradient(135deg, #181C5F 0%, #2E4C7B 100%)",
		background: "url('./bg.jpg') center/cover no-repeat fixed",
		color: "white",
		overflowX: "hidden",
	},

	topNav: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: "2.2rem 2.3rem", // Increased horizontal padding
	},

	brandText: {
		color: "white",
		margin: 0,
		font: "normal normal bold 25px/50px Montserrat",
		letterSpacing: "0px",
	},

	userNav: {
		display: "flex",
		alignItems: "center",
		gap: "1rem",
	},

	profileInfo: {
		textAlign: "right",
	},

	userName: {
		color: "white",
		margin: 0,
		fontSize: "18px",
		fontWeight: "bold",
	},

	userEmail: {
		fontSize: "12px",
		color: "rgba(255, 255, 255, 0.9)",
		margin: 0,
	},

	profileAvatar: {
		width: "50px",
		height: "50px",
		borderRadius: "50%",
		border: "2px solid rgba(255, 255, 255, 0.2)",
		overflow: "hidden",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},

	profileImage: {
		width: "100%",
		height: "100%",
		objectFit: "cover",
	},

	avatarFallback: {
		width: "100%",
		height: "100%",
		background: "#f3f4f6",
		color: "#1f2937",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		fontWeight: "700",
		fontSize: "1rem",
	},

	logoutButton: {
		background: "transparent",
		color: "white",
		border: "none",
		cursor: "pointer",
		padding: "0.5rem",
		borderRadius: "4px",
		transition: "background 0.2s ease",
		fontWeight: "600",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		":hover": {
			background: "rgba(255, 255, 255, 0.1)",
		},
	},

	logoutIcon: {
		width: "20px",
		height: "20px",
		filter: "brightness(0) invert(1)", // Makes the SVG white
	},

	// Mobile hamburger menu styles
	mobileMenuContainer: {
		position: "relative",
	},

	hamburgerButton: {
		background: "transparent",
		border: "none",
		cursor: "pointer",
		padding: "0.5rem",
		display: "flex",
		flexDirection: "column",
		gap: "4px",
		minWidth: "44px",
		minHeight: "44px",
		justifyContent: "center",
		alignItems: "center",
	},

	hamburgerLine: {
		width: "24px",
		height: "2px",
		backgroundColor: "white",
		borderRadius: "1px",
		transition: "all 0.3s ease",
	},

	mobileMenu: {
		position: "absolute",
		top: "100%",
		right: "0",
		marginTop: "0.5rem",
		background: "rgba(30, 30, 60, 0.95)",
		border: "1px solid rgba(255, 255, 255, 0.2)",
		borderRadius: "12px",
		padding: "1rem",
		minWidth: "220px",
		backdropFilter: "blur(10px)",
		zIndex: 1000,
		boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
	},

	mobileUserProfile: {
		display: "flex",
		alignItems: "center",
		gap: "0.75rem",
		marginBottom: "1rem",
		paddingBottom: "1rem",
		borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
	},

	mobileProfileImage: {
		width: "40px",
		height: "40px",
		borderRadius: "50%",
		objectFit: "cover",
	},

	mobileAvatarFallback: {
		width: "40px",
		height: "40px",
		borderRadius: "50%",
		background: "#f3f4f6",
		color: "#1f2937",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		fontWeight: "700",
		fontSize: "1rem",
	},

	mobileUserInfo: {
		flex: 1,
	},

	mobileUserName: {
		color: "white",
		fontSize: "16px",
		fontWeight: "600",
		margin: 0,
	},

	mobileUserEmail: {
		fontSize: "12px",
		color: "rgba(255, 255, 255, 0.7)",
		margin: 0,
		marginTop: "2px",
	},

	mobileLogoutButton: {
		width: "100%",
		background: "transparent",
		border: "1px solid rgba(255, 255, 255, 0.2)",
		color: "white",
		padding: "0.75rem",
		borderRadius: "8px",
		cursor: "pointer",
		fontSize: "14px",
		fontWeight: "500",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: "0.5rem",
		transition: "all 0.2s ease",
		":hover": {
			background: "rgba(255, 255, 255, 0.1)",
		},
	},

	mobileLogoutIcon: {
		width: "16px",
		height: "16px",
		filter: "brightness(0) invert(1)",
	},

	mainContent: {
		padding: "0 4rem 8rem 4rem", // Keep left padding, adjust for meetings extension
		paddingLeft: "108px",
	},

	welcomeAndMeetingsContainer: {
		display: "flex",
		alignItems: "center",
		gap: "3rem",
		// marginBottom: "4rem",
		minHeight: "400px",
		// paddingBottom: "2rem",
		// borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
		marginRight: "-5rem", // Extend meetings section to screen edge
		paddingRight: "1rem", // Add back padding for the divider line
	},

	horizontalDivider: {
		height: "1px",
		background: "rgba(255, 255, 255, 0.2)",
		marginBottom: "2rem",
	},

	welcomeSection: {
		flex: "0 0 auto",
		minWidth: "400px",
		display: "flex",
		flexDirection: "column",
		justifyContent: "center", // Add this to center content vertically
		paddingTop: "3rem",
	},

	welcomeTitle: {
		fontSize: "62px",
		fontWeight: "bold",
		margin: "0 0 1rem 0",
		color: "white",
		lineHeight: "1.1",
	},

	welcomeSubtitle: {
		fontSize: "31px",
		color: "rgba(255, 255, 255, 0.8)",
		fontWeight: "100", // Changed from "400" to "100" for lightest weight
		margin: 0,
		lineHeight: "1.4",
		maxWidth: "387px",
	},

	meetingsWrapper: {
		flex: 1,
		minWidth: 0,
		marginRight: "-4rem", // Extend to screen edge
		paddingRight: "4rem", // Padding for content inside
	},

	bottomSection: {
		display: "grid",
		gridTemplateColumns: "1fr 1fr 1fr",
		gap: "2rem",
		marginTop: "2rem",
		paddingTop: "2rem", // Add padding after the divider
	},

	sectionCard: {
		background: "rgba(255, 255, 255, 0.1)",
		borderRadius: "16px",
		padding: "2rem",
		backdropFilter: "blur(10px)",
		border: "1px solid rgba(255, 255, 255, 0.1)",
		minHeight: "120px",
	},

	sectionTitle: {
		fontSize: "16px",
		fontWeight: "600",
		marginBottom: "1.5rem",
		color: "white",
	},

	calendarConnections: {
		display: "flex",
		flexDirection: "column",
		gap: "0rem",
	},
};

export default Dashboard;
